import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { CircularProgress } from "@mui/material";
import {
  clearAddressList,
  getSearchedPremiseDetails,
  searchPremisesByTerm,
} from "../../../app/redux/slices/app/premiseSlice";
import {
  searchByAddressEmailPhoenumber,
  setSelectedAddressAccount,
  setSelectedAddressServiceAccount,
  setSelectedAccount,
  setUserAccountDetails,
  getAccountDetailsByPhonenumber,
  clearErrorMessage,
} from "../../../app/redux/slices/app/userDetailsSlice";
import { TextMaskCustom } from "../../../app/utils/utils";
import ModalComponent from "../../components/customComponents/ModalComponent/ModalComponent";
import PhoneSearchPopup from "./phoneSearchPopup";
import CustomSnackbar from "../../components/customComponents/CustomSnackbar/index";
import { useFetcher } from "react-router-dom";

const CentricUserSearch = () => {
  const [openAddonModal, setOpenAddonModal] = useState(false);
  const [openPhoneModal, setOpenPhoneModal] = useState(false);

  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { selectedAccount, userAccountDetails } = useSelector(
    (state) => state?.userDetailSlice,
  );
  const [searchText, setSearchText] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [formData, setFormData] = useState({
    address: "",
    phone: "",
    email: "",
    isActive: true,
  });

  const { phoneSearchedUserData } = useSelector(
    (state) => state?.userDetailSlice,
  );

  const userInfo = userAccountDetails?.userAccountInfo;
  const addressDetails = userInfo?.user_address_details || [];
  const userAccounts = userInfo?.user_accounts || [];

  const { error: userAccountError } = userAccountDetails || {};
  const { error: phoneSearchError } = phoneSearchedUserData || {};

  useEffect(() => {
    if (userAccountError && userAccountError !== "") {
      setOpenSnackbar(true);
    } else {
      setOpenSnackbar(false);
    }
  }, [userAccountError]);

  useEffect(() => {
    if (phoneSearchedUserData.loader) return;
    const userData = phoneSearchedUserData;

    if (userData.error && userData.error !== "") {
      setOpenSnackbar(true);
      return;
    }

    const isSuccess = userData.data?.["is-success"];
    const users = userData.data?.data || [];
    const phone = users?.[0]?.phone?.replace(/\D/g, "") || "";

    if (isSuccess === false && userData.error !== "") {
      setOpenSnackbar(true);
      return;
    }

    if (isSuccess === true && users.length > 1) {
      setOpenPhoneModal(true);
      return;
    }

    if (users.length === 1) {
      setFormData((prev) => ({
        ...prev,
        phone,
      }));

      dispatch(
        searchByAddressEmailPhoenumber({
          phone,
          isActive: formData.isActive,
        }),
      );
    }
  }, [phoneSearchedUserData, dispatch, formData.isActive]);

  useEffect(() => {
    if (!userInfo) return;

    setFormData((prev) => ({
      ...prev,
      phone: userInfo?.phone_number || "",
      email: userInfo?.email_id || "",
    }));
  }, [userInfo]);

  const { search_premise, searchedMyAccountdAddress } = useSelector(
    (state) => state.premiseSlice,
  );

  // get account details based on address, phone and email
  useEffect(() => {
    if (formData.address === "" || formData.address.length < 2) return;

    dispatch(
      searchByAddressEmailPhoenumber({
        address: formData.address,
        isActive: formData.isActive,
      }),
    );
  }, [formData.address, formData.isActive, dispatch]);

  useEffect(() => {
    if (addressDetails && addressDetails?.length > 0) {
      var searchedAddress =
        formData.address !== ""
          ? addressDetails?.find((x) => x.street_address === formData.address)
          : addressDetails[0];

      handleAddressAccountChange(searchedAddress);
    }
  }, [addressDetails, dispatch]);

  const searchBtn = () => {
    const { address, phone, email } = formData;
    if (!address && !phone && !email) return;

    if (phone && !address && !email) {
      dispatch(getAccountDetailsByPhonenumber(phone.replace(/\D/g, "")));
      return;
    }

    dispatch(searchByAddressEmailPhoenumber({ ...formData }));
  };

  const handleAddressAccountChange = useCallback(
    (SelectedAddressAccount) => {
      dispatch(setSelectedAddressAccount(SelectedAddressAccount));
      dispatch(
        setSelectedAddressServiceAccount(
          SelectedAddressAccount?.userAddressServcieInfos[0],
        ),
      );

      const selectAccount =
        SelectedAddressAccount?.userAddressServcieInfos[0]?.user_accounts[0];

      if (selectAccount) {
        dispatch(
          setSelectedAccount({
            ...selectAccount,
            address: selectAccount.street_address,
          }),
        );
      } else {
        dispatch(
          setSelectedAccount({
            ...selectAccount,
            address: "",
          }),
        );
      }

      setOpenAddonModal(false);
    },
    [dispatch],
  );

  const resetData = () => {
    setFormData({
      address: "",
      phone: "",
      email: "",
      isActive: true,
    });
    setSearchText("");
    dispatch(getSearchedPremiseDetails(null));
    dispatch(setUserAccountDetails(null));
    dispatch(setSelectedAddressServiceAccount(null));
    dispatch(setSelectedAccount(null));
    dispatch(setSelectedAddressAccount(null));
    dispatch(setSelectedAddressAccount(null));
    dispatch(getAccountDetailsByPhonenumber(null));
  };

  // Create fast lookup map (better if list is large)
  const accountsMap = Object.fromEntries(
    userAccounts.map((acc) => [acc.crm_service_account_id, acc]),
  );

  const setEmailAndPhone = (userData) => {
    setFormData((prev) => ({
      ...prev,
      email: userData?.email,
    }));
    setOpenPhoneModal(false);
    dispatch(
      searchByAddressEmailPhoenumber({
        email: userData?.email,
        isActive: formData.isActive,
      }),
    );
  };

  const statusColors = {
    ACTIVE: "green",
    PENDING: "yellow",
    INACTIVE: "red",
    DELETE: "red",
    SUSPEND: "orange",
  };

  return (
    <>
      <CustomSnackbar
        openSnackbar={openSnackbar}
        severity={"error"}
        message={
          userAccountError ||
          phoneSearchError ||
          "An error occurred while fetching user details. Please try again."
        }
        autoHideDuration={1500}
        handleClose={() => {
          setOpenSnackbar(false);
          dispatch(clearErrorMessage());
        }}
      />

      <div className="dropdown-wrapper gap-2 centric-search">
        <div className="dropdown-choose-wrapper location">
          <div className="dropdown-title">Search By Service Address</div>
          <div className="m-0 w-100">
            <Autocomplete
              freeSolo
              style={{
                width: "100%",
                padding: "0px",
              }}
              value={selectedAccount}
              inputValue={searchText}
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              onInputChange={(event, value, reason) => {
                setSearchText(value);
                if (reason === "clear" || value === "") {
                  dispatch(getSearchedPremiseDetails(null));
                  dispatch(setSelectedAccount(null));
                  resetData();
                  dispatch(clearAddressList());
                  dispatch(getSearchedPremiseDetails(null));
                  return;
                }

                if (value.length > 2) {
                  dispatch(searchPremisesByTerm(value));
                }
              }}
              onChange={(event, value) => {
                // Sync inputValue with selected item label
                var searchedAddress = value
                  ? `${[
                      value?.address || value?.street_address,
                      value?.city,
                      [value?.state, value?.zip].filter(Boolean).join(" "),
                    ]
                      .filter(Boolean)
                      .join(", ")
                      .trim()}`
                  : "";
                setSearchText(searchedAddress);
                if (value && value.street_address) {
                  setFormData({ ...formData, address: value?.street_address });
                  dispatch(getSearchedPremiseDetails(value.crm_premise_id));
                }
              }}
              isOptionEqualToValue={(option, value) =>
                option.crm_premise_id === value.crm_premise_id
              }
              getOptionLabel={(option) => {
                return `${[
                  option?.address ? option?.address : option?.street_address,
                  option?.city,
                  [option?.state, option?.zip].filter(Boolean).join(" "),
                ]
                  .filter(Boolean)
                  .join(", ")
                  .trim()}`;
              }}
              // options={searchedMyAccountdAddress?.data || []}
              // loading={searchedMyAccountdAddress?.loader || false}
              options={search_premise?.data || []}
              loading={search_premise?.loader || false}
              renderInput={(params) => (
                <TextField
                  //onChange={(e) => updateAddress(e)}
                  placeholder="Search Address"
                  className="form-control p-0"
                  {...params}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {searchedMyAccountdAddress?.loader ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    },
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className="dropdown-choose-wrapper phone">
          <div className="dropdown-title">Phone</div>
          <Input
            fullWidth
            value={formData.phone}
            onChange={(e) => {
              if (e.target.value !== "") {
                setFormData({ ...formData, phone: e.target.value });
              } else {
                setFormData({ ...formData, phone: "" });
              }
            }}
            disableUnderline
            inputComponent={TextMaskCustom}
            style={{ backgroundColor: "white" }}
          />
        </div>
        <div className="dropdown-choose-wrapper email">
          <div className="dropdown-title">Email Id</div>
          <input
            type="text"
            className="form-control"
            value={formData.email}
            onChange={(e) => {
              e.preventDefault();
              if (e.target.value !== "") {
                setFormData({ ...formData, email: e.target.value });
              } else {
                setFormData({ ...formData, email: "" });
              }
            }}
            placeholder=""
          />
        </div>
        <div>
          <div className="dropdown-title">
            <label>
              <input
                type="checkbox"
                className="subcategory-checkbox"
                checked={formData.isActive}
                onChange={(e) => {
                  setFormData({ ...formData, isActive: e.target.checked });
                }}
              />
              &nbsp; Active Only
            </label>
          </div>
          <div style={{ display: "flex", gap: "2px" }}>
            <div className="dropdown-choose-wrapper submit-btns search-btn">
              <button
                onClick={searchBtn}
                disabled={
                  formData.address === "" &&
                  formData.email === "" &&
                  formData.phone === ""
                }
                className="btn btn-xs btn-custom btn-icon outline-light"
              >
                {userAccountDetails.loader ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <i className="fa-regular fa-magnifying-glass"></i>
                )}
              </button>
            </div>
            <div className="dropdown-choose-wrapper submit-btns switch-btn">
              <button
                className="btn btn-xs btn-custom btn-icon outline-light"
                data-bs-toggle="tooltip"
                title="Switch account"
                disabled={
                  !userAccountDetails?.userAccountInfo?.user_address_details
                }
                onClick={(e) => {
                  e.preventDefault();
                  setOpenAddonModal(true);
                }}
              >
                <i className="fa-regular fa-arrows-repeat"></i>
              </button>
            </div>
            <div className="dropdown-choose-wrapper submit-btns switch-btn">
              <button
                onClick={resetData}
                className="btn btn-xs btn-custom btn-icon outline-light"
                data-bs-toggle="tooltip"
                title="Reset search"
                data-bs-placement="top"
              >
                <i className="fa-regular fa-eraser"></i>
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}></div>

        <ModalComponent
  openModal={openAddonModal}
  onClose={() => setOpenAddonModal(false)}
  modalId="AddressAccounts"
  mainheading="Switch Account"
  modalContent={
    <div className="table-responsive">
      <table className="table table-bordered align-middle">
        <thead className="table-light">
          <tr>
            <th>Address</th>
            <th>Parent Account</th>
            <th>Account Name</th>
            <th>Service Account</th>
            <th>Billing Account</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {addressDetails.map((item) => {
            const account =
              accountsMap[item?.crm_service_account_id];

            const statusColor =
              statusColors[account?.status] || "yellow";

            const parentAccountNumber =
              item?.userAddressServcieInfos?.[0]?.user_accounts?.[0]
                ?.crm_customer_number || "";

            const serviceAccountNumber =
              item?.userAddressServcieInfos?.[0]?.user_accounts?.[0]
                ?.crm_service_account_number || "";

            const serviceAccountName =
              item?.userAddressServcieInfos?.[0]?.user_accounts?.[0]
                ?.crm_service_account_name || "";

            const billingAccountNumber =
              item?.userAddressServcieInfos?.[0]?.user_accounts?.[0]
                ?.crm_billing_account_number || "";

            return (
              <tr key={item?.crm_service_account_id}>
                {/* Address */}
                <td>
                  {item?.street_address}, {item?.city},{" "}
                  {[item?.state, item?.zip]
                    .filter(Boolean)
                    .join(" ")}
                </td>

                {/* Parent Account */}
                <td>{parentAccountNumber}</td>

                {/* Service Account */}
                <td>{serviceAccountName}</td>
                <td>{serviceAccountNumber}</td>

                {/* Billing Account */}
                <td>{billingAccountNumber}</td>

                {/* Status */}
                <td>
                  <span
                    style={{
                      backgroundColor: statusColor,
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                      border: "1px solid black",
                      display: "inline-block",
                    }}
                  />
                </td>

                {/* Action */}
                <td>
                  <button
                    className="btn btn-custom startlit_navy"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddressAccountChange(item);
                    }}
                    disabled={
                      serviceAccountNumber ===
                      selectedAccount?.crm_service_account_number
                    }
                  >
                    Choose
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  }
/>

        <PhoneSearchPopup
          openModal={openPhoneModal}
          setOpenPhoneModal={setOpenPhoneModal}
          phoneSearchedUserData={phoneSearchedUserData}
          setEmailAndPhone={setEmailAndPhone}
        />
      </div>
    </>
  );
};

export default CentricUserSearch;
